import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { BrandMark } from '@/components/shared/BrandMark';
import { Button } from '@/components/shared/Button';
import { Panel } from '@/components/shared/Panel';
import { useAuth } from '@/hooks/useAuth';

export function OnboardingPage(): JSX.Element {
  const { isLoading, isSignedIn, isApproved, needsOnboarding, completeOnboarding } = useAuth();
  const [inGameName, setInGameName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-page__backdrop" />
        <Panel title="Loading onboarding" description="Checking your guild profile before continuing." />
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!needsOnboarding) {
    return <Navigate to={isApproved ? '/masses' : '/pending-approval'} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!inGameName.trim()) {
      setError('Enter your In-Game Name before continuing.');
      return;
    }

    setIsSaving(true);

    try {
      await completeOnboarding(inGameName);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save your In-Game Name right now.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__backdrop" />
      <Panel
        title="Complete Your Profile"
        description="Set your In-Game Name once so officers can recognize you in mass rosters. You will not be able to change it yourself later."
      >
        <form className="auth-card" onSubmit={handleSubmit}>
          <BrandMark />
          <label className="field">
            <span className="field__label">In-Game Name</span>
            <input
              className="field__input"
              value={inGameName}
              onChange={(inputEvent) => setInGameName(inputEvent.target.value)}
              placeholder="Enter your Albion character name"
              autoComplete="off"
              maxLength={50}
            />
          </label>
          {error ? <p className="form-footnote form-footnote--error">{error}</p> : null}
          <Button type="submit" fullWidth disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save In-Game Name'}
          </Button>
        </form>
      </Panel>
    </div>
  );
}
